import { useEffect, useState, type FormEvent } from "react";
import { isDocumentedImageSize } from "@tandem/image-studio-shared";
import { api, ApiClientError } from "../api.js";
import { useAuth } from "../auth.js";
import type {
  StudioSettings,
  ManagedUser,
  ManagedSession,
  PlatformPreset,
} from "../types.js";
import { formatDate } from "../ui.js";
import "./settings.css";

const qualityNames = {
  draft: "Экономичное",
  standard: "Стандартное",
  maximum: "Максимальное",
};
const errors: Record<string, string> = {
  SETTINGS_CHANGED:
    "Настройки уже изменены другим администратором. Обновите страницу.",
  USER_CHANGED:
    "Данные пользователя изменились. Закройте форму и обновите список.",
  EMAIL_ALREADY_EXISTS: "Пользователь с таким email уже существует.",
  CANNOT_DISABLE_SELF:
    "Нельзя отключить свою учётную запись или убрать у себя права администратора.",
  LAST_ADMIN_REQUIRED:
    "В сервисе должен остаться хотя бы один активный администратор.",
  SETTING_CAPABILITY_UNAVAILABLE:
    "Этот формат или качество недоступны у текущего провайдера.",
  SETTING_PLATFORM_UNAVAILABLE:
    "Выбранный размер больше недоступен. Выберите другой.",
  INVALID_CONCURRENCY_LIMIT:
    "Лимит на пользователя не может превышать общий лимит.",
  ADMIN_RATE_LIMITED: "Слишком много изменений. Подождите минуту.",
};
function errorText(error: unknown) {
  return error instanceof ApiClientError
    ? (errors[error.code] ??
        "Не удалось сохранить изменение. Проверьте поля и права доступа.")
    : "Не удалось выполнить запрос. Попробуйте снова.";
}

export function SettingsScreen() {
  const auth = useAuth();
  const admin = auth.user?.role === "admin";
  const [settings, setSettings] = useState<StudioSettings | null>(null);
  const [platforms, setPlatforms] = useState<PlatformPreset[]>([]);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sessions, setSessions] = useState<ManagedSession[]>([]);
  const [sessionUser, setSessionUser] = useState("");
  const [sessionName, setSessionName] = useState("Мои сессии");
  const [editing, setEditing] = useState<ManagedUser | "new" | null>(null);
  const [reset, setReset] = useState<ManagedUser | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  async function loadUsers() {
    if (admin) {
      const result = await api.listUsers(page);
      setUsers(result.items);
      setTotal(result.total);
    }
  }
  async function loadSessions() {
    setSessions((await api.listSessions(sessionUser || undefined)).items);
  }
  useEffect(() => {
    let active = true;
    void Promise.all([api.getSettings(), api.listPlatformPresets()])
      .then(([s, p]) => {
        if (active) {
          setSettings(s);
          setPlatforms(
            p.items.filter(
              (v) =>
                v.slug !== "custom" && isDocumentedImageSize(v.width, v.height),
            ),
          );
        }
      })
      .catch(() => {
        if (active)
          setError("Не удалось загрузить настройки. Обновите страницу.");
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    void loadUsers().catch(() =>
      setError("Не удалось загрузить пользователей."),
    );
  }, [admin, page]);
  useEffect(() => {
    let active = true;
    setSessions([]);
    void api
      .listSessions(sessionUser || undefined)
      .then((r) => {
        if (active) setSessions(r.items);
      })
      .catch(() => {
        if (active) setError("Не удалось загрузить сессии.");
      });
    return () => {
      active = false;
    };
  }, [sessionUser]);
  async function action(work: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await work();
      setNotice(message);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  function change<K extends keyof StudioSettings>(
    key: K,
    value: StudioSettings[K],
  ) {
    setSettings((s) => (s ? { ...s, [key]: value } : s));
  }
  return (
    <div className="studio-settings">
      <div className="page-heading">
        <div>
          <h1>Настройки</h1>
          <p>Параметры генерации, доступ к сервису и очередь задач</p>
        </div>
      </div>
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <p className="settings-notice" role="status">
          {notice}
        </p>
      )}
      <div className="settings-columns">
        <form
          className="panel settings-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (settings)
              void action(
                async () => setSettings(await api.saveSettings(settings)),
                "Настройки сохранены. Они применятся к новым формам генерации.",
              );
          }}
        >
          <h2>Параметры по умолчанию</h2>
          <p>
            Для новой формы. Сохранённый черновик и выбранный шаблон имеют
            приоритет; готовые изображения не изменятся.
          </p>
          {!settings ? (
            <p>Загрузка…</p>
          ) : (
            <>
              <fieldset disabled={!admin || busy}>
                <div className="form-grid">
                  <label>
                    Качество
                    <select
                      value={settings.defaultQuality}
                      onChange={(e) =>
                        change(
                          "defaultQuality",
                          e.target.value as StudioSettings["defaultQuality"],
                        )
                      }
                    >
                      {settings.capabilities.qualities.map((q) => (
                        <option key={q} value={q}>
                          {qualityNames[q]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Количество изображений
                    <input
                      type="number"
                      min={1}
                      max={20}
                      required
                      value={settings.defaultCount}
                      onChange={(e) =>
                        change("defaultCount", Number(e.target.value))
                      }
                    />
                  </label>
                  <label>
                    Формат
                    <select
                      value={settings.defaultOutputFormat}
                      onChange={(e) =>
                        change(
                          "defaultOutputFormat",
                          e.target
                            .value as StudioSettings["defaultOutputFormat"],
                        )
                      }
                    >
                      {settings.capabilities.outputFormats.map((f) => (
                        <option key={f} value={f}>
                          {f.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Размер
                    <select
                      value={settings.defaultPlatformId ?? ""}
                      onChange={(e) =>
                        change("defaultPlatformId", e.target.value || null)
                      }
                    >
                      <option value="">Первый доступный</option>
                      {platforms.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} · {p.width}×{p.height}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="settings-section">
                  <h2>Очередь генераций</h2>
                  <p>
                    Лимиты действуют на новые запуски во всех worker-процессах.
                    Уже запущенные задачи завершаются. Фактический параллелизм
                    также ограничен сервером; текущий Hermes обрабатывает по
                    одной задаче.
                  </p>
                  <div className="form-grid">
                    <label>
                      Общий лимит одновременно
                      <input
                        type="number"
                        min={1}
                        max={4}
                        required
                        value={settings.maxConcurrentJobs}
                        onChange={(e) =>
                          change("maxConcurrentJobs", Number(e.target.value))
                        }
                      />
                    </label>
                    <label>
                      Лимит на пользователя
                      <input
                        type="number"
                        min={1}
                        max={settings.maxConcurrentJobs}
                        required
                        value={settings.maxConcurrentPerUser}
                        onChange={(e) =>
                          change("maxConcurrentPerUser", Number(e.target.value))
                        }
                      />
                    </label>
                  </div>
                  <label className="settings-checkbox">
                    <input
                      type="checkbox"
                      checked={settings.queuePaused}
                      onChange={(e) => change("queuePaused", e.target.checked)}
                    />
                    Приостановить запуск новых задач
                  </label>
                  {settings.queuePaused && (
                    <p className="settings-warning">
                      Новые задания будут ждать в очереди до снятия паузы.
                    </p>
                  )}
                </div>
              </fieldset>
              {admin ? (
                <button className="primary compact-button" disabled={busy}>
                  Сохранить настройки
                </button>
              ) : (
                <p>Общие настройки может менять только администратор.</p>
              )}
            </>
          )}
        </form>
        <section className="panel settings-card">
          <div className="settings-title">
            <div>
              <h2>{sessionName}</h2>
              <p>
                До 100 активных сессий. Пароли и токены здесь не отображаются.
              </p>
            </div>
            {sessionUser && (
              <button
                className="secondary compact-button"
                onClick={() => {
                  setSessionUser("");
                  setSessionName("Мои сессии");
                }}
              >
                Мои сессии
              </button>
            )}
          </div>
          <button
            className="secondary compact-button"
            disabled={busy || !sessions.some((s) => !s.current)}
            onClick={() => {
              if (
                window.confirm(
                  "Завершить остальные сессии? Потребуется повторный вход.",
                )
              )
                void action(async () => {
                  await api.revokeOtherSessions(sessionUser || undefined);
                  await loadSessions();
                }, "Остальные сессии завершены.");
            }}
          >
            Завершить остальные сессии
          </button>
          <div className="settings-session-list">
            {sessions.map((s) => (
              <article key={s.id} className="settings-session">
                <div>
                  <strong>
                    {s.current ? "Текущая сессия" : "Сессия"} ·{" "}
                    {s.id.slice(0, 8)}
                  </strong>
                  <p>
                    Вход: {formatDate(s.createdAt, true)}
                    <br />
                    Активность: {formatDate(s.lastSeenAt, true)}
                    <br />
                    Истекает: {formatDate(s.expiresAt, true)}
                  </p>
                </div>
                {s.current ? (
                  <span className="status status-active">Это устройство</span>
                ) : (
                  <button
                    className="secondary compact-button"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm("Завершить эту сессию?"))
                        void action(async () => {
                          await api.revokeSession(s.id);
                          await loadSessions();
                        }, "Сессия завершена.");
                    }}
                  >
                    Завершить
                  </button>
                )}
              </article>
            ))}
            {!sessions.length && <p>Активных сессий нет.</p>}
          </div>
        </section>
      </div>
      {admin && (
        <section className="panel settings-card">
          <div className="settings-title">
            <div>
              <h2>Пользователи</h2>
              <p>
                {total} учётных записей. Отключение сохраняет историю и
                завершает сессии пользователя.
              </p>
            </div>
            <button
              className="primary compact-button"
              disabled={busy}
              onClick={() => setEditing("new")}
            >
              Добавить пользователя
            </button>
          </div>
          <div className="settings-user-list">
            {users.map((u) => (
              <article key={u.id} className="settings-user">
                <div>
                  <strong>{u.displayName}</strong>
                  <p>{u.email}</p>
                </div>
                <span>
                  {u.role === "admin" ? "Администратор" : "Пользователь"} ·{" "}
                  {u.status === "active" ? "Активен" : "Отключён"}
                </span>
                <div className="settings-actions">
                  <button
                    className="secondary compact-button"
                    disabled={busy}
                    onClick={() => setEditing(u)}
                  >
                    Изменить
                  </button>
                  <button
                    className="secondary compact-button"
                    disabled={busy}
                    onClick={() => {
                      setSessionUser(u.id);
                      setSessionName(`Сессии: ${u.displayName}`);
                    }}
                  >
                    Сессии
                  </button>
                  {u.id !== auth.user?.id && (
                    <button
                      className="secondary compact-button"
                      disabled={busy}
                      onClick={() => {
                        setReset(u);
                        setPassword("");
                      }}
                    >
                      Сменить пароль
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
          <div className="settings-actions">
            <button
              className="secondary compact-button"
              disabled={page <= 1 || busy}
              onClick={() => setPage((p) => p - 1)}
            >
              Назад
            </button>
            <span>
              Страница {page} из {Math.max(1, Math.ceil(total / 20))}
            </span>
            <button
              className="secondary compact-button"
              disabled={page * 20 >= total || busy}
              onClick={() => setPage((p) => p + 1)}
            >
              Далее
            </button>
          </div>
        </section>
      )}
      {editing && (
        <UserEditor
          key={editing === "new" ? "new" : editing.id}
          user={editing}
          ownId={auth.user!.id}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await loadUsers();
            await loadSessions();
            setNotice("Пользователь сохранён.");
          }}
        />
      )}
      {reset && (
        <div className="dialog-backdrop">
          <form
            className="dialog compact-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Смена пароля"
            onSubmit={(e) => {
              e.preventDefault();
              void action(async () => {
                await api.resetUserPassword(reset.id, password);
                setPassword("");
                setReset(null);
                await loadUsers();
                await loadSessions();
              }, "Пароль изменён, прежние сессии завершены.");
            }}
          >
            <h2>Новый пароль: {reset.displayName}</h2>
            <p>
              Все сессии пользователя будут завершены. Передайте новый пароль
              лично.
            </p>
            <label>
              Новый пароль
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && (
              <p role="alert" className="form-error">
                {error}
              </p>
            )}
            <div className="dialog-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setReset(null);
                  setPassword("");
                }}
              >
                Отмена
              </button>
              <button className="primary compact-button" disabled={busy}>
                Изменить пароль
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function UserEditor({
  user,
  ownId,
  onClose,
  onSaved,
}: {
  user: ManagedUser | "new";
  ownId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [email, setEmail] = useState(user === "new" ? "" : user.email);
  const [name, setName] = useState(user === "new" ? "" : user.displayName);
  const [role, setRole] = useState<"admin" | "user">(
    user === "new" ? "user" : user.role,
  );
  const [status, setStatus] = useState<"active" | "disabled">(
    user === "new" ? "active" : user.status,
  );
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const securityChange =
    user !== "new" && (user.role !== role || user.status !== status);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (user === "new")
        await api.createUser({ email, displayName: name, role, password });
      else
        await api.updateUser({
          ...user,
          email,
          displayName: name,
          role,
          status,
        });
      setPassword("");
      await onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog compact-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={
          user === "new" ? "Новый пользователь" : "Редактирование пользователя"
        }
        onSubmit={(e) => void submit(e)}
      >
        <h2>
          {user === "new"
            ? "Новый пользователь"
            : "Редактирование пользователя"}
        </h2>
        <fieldset disabled={busy}>
          <label>
            Имя
            <input
              autoFocus
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label>
            Email
            <input
              type="email"
              required
              maxLength={320}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Роль
            <select
              disabled={user !== "new" && user.id === ownId}
              value={role}
              onChange={(e) => {
                setRole(e.target.value as typeof role);
                setConfirmed(false);
              }}
            >
              <option value="user">Пользователь</option>
              <option value="admin">Администратор</option>
            </select>
          </label>
          {user === "new" ? (
            <label>
              Пароль
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <small>От 12 до 128 символов. Передайте пароль лично.</small>
            </label>
          ) : (
            <label>
              Статус
              <select
                disabled={user.id === ownId}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as typeof status);
                  setConfirmed(false);
                }}
              >
                <option value="active">Активен</option>
                <option value="disabled">Отключён</option>
              </select>
            </label>
          )}
          {securityChange && (
            <label className="settings-checkbox">
              <input
                type="checkbox"
                required
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              Подтверждаю изменение доступа. При отключении или смене роли
              сессии завершатся.
            </label>
          )}
        </fieldset>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" disabled={busy} onClick={onClose}>
            Отмена
          </button>
          <button
            className="primary compact-button"
            disabled={busy || (securityChange && !confirmed)}
          >
            Сохранить пользователя
          </button>
        </div>
      </form>
    </div>
  );
}
